/**
 * Custom hook for material autocomplete
 * 
 * Provides debounced autocomplete suggestions for material names
 */
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { fetchAllMaterials } from '../api/articleApi'
import { type Material } from '../utils/types'

export interface MaterialSuggestion {
  id: string
  name: string
  self: string
}

export interface MaterialAutocompleteResponse {
  suggestions: MaterialSuggestion[]
  total: number
}

/**
 * Hook for material name autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 * 
 * @example
 * ```tsx
 * const { suggestions, isLoading, updateQuery } = useMaterialAutocomplete()
 * 
 * <input onChange={(e) => updateQuery(e.target.value)} />
 * {suggestions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
 * ```
 */
export function useMaterialAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  // Fetch all materials
  const { data: materials, isLoading, error } = useQuery<Material[], Error>({
    queryKey: ['materials-autocomplete', 'all'],
    queryFn: () => fetchAllMaterials({}),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  })

  // Filter materials based on query
  const filteredSuggestions = useMemo(() => {
    if (!materials) return []
    
    const queryLower = debouncedQuery.toLowerCase()
    const filtered = materials
      .filter(material => 
        !queryLower || material.name.toLowerCase().includes(queryLower)
      )
      .slice(0, size)
      .map(material => ({
        id: material.key,
        name: material.name,
        self: material.self,
      }))
    
    return filtered
  }, [materials, debouncedQuery, size])

  return {
    suggestions: filteredSuggestions,
    total: filteredSuggestions.length,
    isLoading: isLoading,
    error,
    updateQuery: setQuery,
    query,
  }
}
